/**
 * Fin St's **Quarters** view: the last eight quarters as columns, in the same
 * shape as the Years view's `annual` rows, so one table renders both.
 *
 * Built on the client from what `/sym/fundamentals` already sends — `periods`
 * (twelve statement quarters) and `earnings` (reported EPS per announcement) —
 * so no server change (owner, 2026-10-07: ratios that need a year or a price,
 * like P/E and ROE, are Years-only).
 *
 * Every `% chg` here follows the owner's rule for EPS YoY/QoQ in the screener
 * (`docs/features/fundamentals.md`): a loss base divides by its size, a zero
 * base by one cent, so a turnaround reads as growth rather than as a blank.
 */

import type { AnnualRow, EarningsQuarter } from "../api/symbols";

/** Quarters shown (owner, 2026-10-07). */
export const QUARTERS_SHOWN = 8;

/** The smallest step a reported EPS can take; the vendor rounds to cents. */
const EPS_TICK = 0.01;
const YEAR_DAYS = 365;
const YEAR_SLACK_DAYS = 45;
const DAY_MS = 86_400_000;
/**
 * A report this soon after the previous one is a duplicate and is skipped; the
 * earlier stands (owner, 2026-10-07). CRCL's IPO-day row, 20 days after its
 * real Q1. Same rule as `derive.DUPLICATE_REPORT_DAYS` on the server.
 */
export const DUPLICATE_REPORT_DAYS = 30;

/** Reported quarters oldest first, with a report under 30 days after the previous row dropped. */
export function distinctReports<T extends { date: string }>(reports: readonly T[]): T[] {
  const sorted = [...reports].sort((a, b) => a.date.localeCompare(b.date));
  return sorted.filter((r, i) => {
    if (i === 0) return true;
    const now = ms(r.date);
    const before = ms(sorted[i - 1]!.date);
    return now === null || before === null || (now - before) / DAY_MS >= DUPLICATE_REPORT_DAYS;
  });
}

type Period = Record<string, number | string | boolean | null>;

/** The change from `old` to `new` as a fraction, by the owner's loss rule. */
export function change(next: number | null, old: number | null): number | null {
  if (next === null || old === null) return null;
  if (old === 0) return next === 0 ? 0 : (next - old) / EPS_TICK;
  return (next - old) / Math.abs(old);
}

const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

const ms = (iso: unknown): number | null => {
  if (typeof iso !== "string") return null;
  const t = Date.parse(iso.slice(0, 10));
  return Number.isFinite(t) ? t : null;
};

/**
 * The index of the row nearest a year before `rows[i]` by `at`, within ±45
 * days — by date, not four back, so a missing quarter cannot pair the wrong two.
 */
function yearAgo<T>(rows: readonly T[], i: number, at: (row: T) => number | null): number {
  const when = at(rows[i]!);
  if (when === null) return -1;
  let best = -1;
  let bestOff = Infinity;
  for (let j = 0; j < i; j++) {
    const t = at(rows[j]!);
    if (t === null) continue;
    const off = Math.abs((when - t) / DAY_MS - YEAR_DAYS);
    if (off <= YEAR_SLACK_DAYS && off < bestOff) {
      best = j;
      bestOff = off;
    }
  }
  return best;
}

/**
 * `"Q2'26"` — the calendar quarter **most of the period falls in**, read as the
 * month 45 days before the period end (owner, 2026-10-07: Q labels).
 *
 * Not the quarter of the end month: NVDA's quarter to 2026-07-26 runs May to
 * July and is "Q2 '26" where analysts publish it (and on the site the owner
 * compares against), while its end month would say Q3. A December or June
 * year-end comes out the same either way.
 */
export function quarterLabel(periodEnd: string): string {
  const t = ms(periodEnd);
  if (t === null) return periodEnd;
  const mid = new Date(t - 45 * DAY_MS);
  const quarter = Math.floor(mid.getUTCMonth() / 3) + 1;
  return `Q${quarter}'${String(mid.getUTCFullYear()).slice(2)}`;
}

/**
 * The quarter columns, oldest first, plus the Years view's `TTM` column if it
 * has one. Values are keyed by the metric ids the rows name, so `YearRow`
 * formats them with the same metric definitions as the Years view.
 */
export function quarterColumns(
  periods: readonly Period[],
  earnings: readonly EarningsQuarter[] = [],
  ttm?: AnnualRow,
  shown = QUARTERS_SHOWN,
): AnnualRow[] {
  const quarters = [...periods]
    .filter((p) => typeof p.periodEnd === "string")
    .sort((a, b) => String(a.periodEnd).localeCompare(String(b.periodEnd)));
  const reports = [...earnings]
    .filter((e) => num(e.epsActual) !== null)
    .sort((a, b) => a.date.localeCompare(b.date));
  const byPeriodEnd = new Map(reports.filter((e) => e.periodEnd).map((e) => [e.periodEnd!, e]));
  // Revenue % chg from the **announced** revenue (owner, 2026-10-08), the
  // screener's Rev YoY/QoQ exactly; the statement's own only where a quarter
  // has no announcement matched to it.
  const revenueGrowth = reportedGrowth(earnings, reportedRevenue);

  const columns = quarters.map((p, i): AnnualRow => {
    const revenue = num(p.revenue);
    const ago = yearAgo(quarters, i, (q) => ms(q.periodEnd));
    const prior = ago >= 0 ? quarters[ago]! : undefined;
    const before = i > 0 ? quarters[i - 1]! : undefined;
    const share = (v: unknown) => {
      const x = num(v);
      return x !== null && revenue ? x / revenue : null;
    };

    // Reported EPS is the announcement matched to this quarter by the server.
    const report = byPeriodEnd.get(String(p.periodEnd));
    const r = report ? reports.indexOf(report) : -1;
    const reportedAgo = r >= 0 ? yearAgo(reports, r, (e) => ms(e.date)) : -1;
    const reported = report ? num(report.epsActual) : null;
    const announced = report ? revenueGrowth.get(report.date) : undefined;

    const cfo = num(p.cashFromOperating);
    const capex = num(p.capitalExpenditure);
    const dividends = num(p.dividendsPaid);
    const shares = num(p.sharesDiluted);

    return {
      label: quarterLabel(String(p.periodEnd)),
      periodEnd: String(p.periodEnd),
      ...(typeof p.availableOn === "string" ? { availableOn: p.availableOn } : {}),
      ...(typeof p.pit === "boolean" ? { pit: p.pit } : {}),
      revenue,
      revenueGrowth: announced
        ? announced.yoy
        : change(revenue, prior ? num(prior.revenue) : null),
      revenueQoQ: announced
        ? announced.qoq
        : change(revenue, before ? num(before.revenue) : null),
      epsReported: reported,
      epsReportedYoY: change(reported, reportedAgo >= 0 ? num(reports[reportedAgo]!.epsActual) : null),
      epsReportedQoQ: change(reported, r > 0 ? num(reports[r - 1]!.epsActual) : null),
      eps: num(p.epsDiluted),
      epsGrowth: change(num(p.epsDiluted), prior ? num(prior.epsDiluted) : null),
      grossMargin: share(p.grossProfit),
      operatingMargin: share(p.operatingIncome),
      netMargin: share(p.netIncome),
      // Capex is stored negative (cash out), so FCF is the sum.
      freeCashFlow: cfo !== null && capex !== null ? cfo + capex : null,
      dividendPerShare:
        dividends !== null && shares ? Math.abs(dividends) / shares : null,
    };
  });

  const recent = columns.slice(-shown);
  return ttm && ttm.label === "TTM" ? [...recent, ttm] : recent;
}

/**
 * True when "the next report" is one already in: a reported quarter within
 * {@link DUPLICATE_REPORT_DAYS} of it (owner, 2026-10-28). The vendor's next
 * date is refreshed nightly, so on the day a company reports — and until the
 * next pass — it still names that report, with that report's consensus. Shown,
 * it read as the company reporting twice.
 */
export function alreadyReported(earnings: readonly EarningsQuarter[], next: unknown): boolean {
  const when = ms(next);
  if (when === null) return false;
  return earnings.some((e) => {
    const t = ms(e.date);
    return t !== null && num(e.epsActual) !== null && Math.abs(when - t) / DAY_MS < DUPLICATE_REPORT_DAYS;
  });
}

/** Days from one quarter end to the next, near enough to place an estimate. */
const QUARTER_DAYS = 91;
/** How far past the estimated quarter end the next report may fall and still be its report. */
const REPORT_LAG_DAYS = 120;

const isoDay = (t: number): string => new Date(t).toISOString().slice(0, 10);

/**
 * The consensus for the quarter not yet reported, as one more column after the
 * newest (owner, 2026-10-28): `Est EPS` and `Est revenue` in the EPS (reported)
 * and Revenue rows, each with its YoY and QoQ against the reported columns
 * beside it. `estimate: true` is what the table draws in yellow.
 *
 * **Only where the next report is the next quarter.** The estimate names no
 * quarter, so it is placed one quarter after the newest statement, and dropped
 * if the next earnings date is not within 120 days of that quarter's end — a
 * report we have not ingested yet would otherwise put next quarter's consensus
 * under this quarter's label.
 *
 * `columns` are `quarterColumns` (a trailing TTM is ignored), oldest first.
 */
export function quarterEstimate(
  columns: readonly AnnualRow[],
  metrics: Record<string, unknown>,
  earnings: readonly EarningsQuarter[] = [],
): AnnualRow | null {
  const eps = num(metrics.epsEstimate);
  const revenue = num(metrics.revenueEstimate);
  if (eps === null && revenue === null) return null;
  if (alreadyReported(earnings, metrics.nextEarningsDate)) return null;
  const quarters = columns.filter((c) => c.label !== "TTM" && !c.estimate);
  const last = quarters.at(-1);
  const lastEnd = last ? ms(last.periodEnd) : null;
  if (!last || lastEnd === null) return null;
  const end = lastEnd + QUARTER_DAYS * DAY_MS;
  const report = ms(metrics.nextEarningsDate);
  if (report === null || report < end || (report - end) / DAY_MS > REPORT_LAG_DAYS) return null;

  const ago = yearAgo([...quarters, { label: "", periodEnd: isoDay(end) }], quarters.length, (q) => ms(q.periodEnd));
  const prior = ago >= 0 ? quarters[ago] : undefined;
  return {
    label: quarterLabel(isoDay(end)),
    periodEnd: isoDay(end),
    estimate: true,
    estimateDate: String(metrics.nextEarningsDate).slice(0, 10),
    estimateNote: `Consensus for the quarter reporting ${String(metrics.nextEarningsDate).slice(0, 10)}`,
    revenue,
    revenueGrowth: change(revenue, prior ? num(prior.revenue) : null),
    revenueQoQ: change(revenue, num(last.revenue)),
    epsReported: eps,
    epsReportedYoY: change(eps, prior ? num(prior.epsReported) : null),
    epsReportedQoQ: change(eps, num(last.epsReported)),
  };
}

/**
 * The fiscal year the next report closes, as one more column after the newest
 * year (owner, 2026-10-28): the quarters of it already reported **plus** the
 * quarter estimate. Revenue against last year's revenue, EPS as reported EPS
 * summed against last year's four reported quarters — the estimate is the
 * reported (adjusted) figure, so a GAAP year beside it would be a different
 * measure.
 *
 * **Only when it adds up to a full year**: the estimate's quarter ends the
 * fiscal year and exactly three reported quarters sit after the last one. Any
 * other quarter would put two or three quarters beside full years.
 *
 * `annual` is the Years rows (a trailing TTM is ignored); `quarters` is every
 * quarter column, unsliced.
 */
export function yearEstimate(
  annual: readonly AnnualRow[],
  quarters: readonly AnnualRow[],
  estimate: AnnualRow | null,
): AnnualRow | null {
  if (!estimate) return null;
  const years = annual.filter((y) => y.label !== "TTM");
  const last = years.at(-1);
  const fyEnd = last ? ms(last.periodEnd) : null;
  const estEnd = ms(estimate.periodEnd);
  if (!last || fyEnd === null || estEnd === null) return null;
  if (Math.abs((estEnd - fyEnd) / DAY_MS - YEAR_DAYS) > YEAR_SLACK_DAYS) return null;

  const reported = quarters.filter((q) => q.label !== "TTM" && !q.estimate);
  const within = (from: number, to: number) =>
    reported.filter((q) => {
      const t = ms(q.periodEnd);
      return t !== null && t > from && t <= to;
    });
  const sofar = within(fyEnd, estEnd);
  if (sofar.length !== 3) return null;

  const sum = (rows: readonly AnnualRow[], id: string, plus: unknown): number | null => {
    const values = [...rows.map((r) => num(r[id])), num(plus)];
    return values.every((v) => v !== null) ? values.reduce((a, b) => a! + b!, 0)! : null;
  };
  const revenue = sum(sofar, "revenue", estimate.revenue);
  const eps = sum(sofar, "epsReported", estimate.epsReported);
  const before = within(fyEnd - YEAR_DAYS * DAY_MS - YEAR_SLACK_DAYS * DAY_MS, fyEnd);
  const lastEps = before.length === 4 ? sum(before, "epsReported", 0) : null;
  if (revenue === null && eps === null) return null;

  const year = Number(last.label);
  return {
    label: Number.isFinite(year) ? String(year + 1) : String(new Date(estEnd).getUTCFullYear()),
    periodEnd: estimate.periodEnd,
    estimate: true,
    estimateDate: estimate.estimateDate,
    estimateNote:
      `${sofar.map((q) => q.label).join(", ")} reported + ${estimate.label} consensus. ` +
      "EPS is reported (adjusted) EPS, not GAAP.",
    revenue,
    revenueGrowth: change(revenue, num(last.revenue)),
    eps,
    epsGrowth: change(eps, lastEps),
  };
}

/**
 * `columns` with `estimate` placed after the newest period — before a trailing
 * TTM, so the table's period order puts it last on screen.
 */
export function withEstimate(columns: readonly AnnualRow[], estimate: AnnualRow | null): AnnualRow[] {
  if (!estimate) return [...columns];
  return columns.at(-1)?.label === "TTM"
    ? [...columns.slice(0, -1), estimate, columns.at(-1)!]
    : [...columns, estimate];
}

/**
 * Reported-EPS YoY and QoQ for **every** report, keyed by its date — what the
 * chart's `E` markers and their popup show (owner, 2026-10-07).
 *
 * The same rules as the screener's EPS YoY/QoQ and the Quarters view: QoQ
 * against the report before, YoY against the report nearest 365 days earlier
 * (±45), and the loss rule in {@link change}. One function, so the number
 * above an `E` and the one in its popup cannot disagree.
 */
export function reportedGrowth(
  earnings: readonly EarningsQuarter[],
  value: (e: EarningsQuarter) => number | null = (e) => num(e.epsActual),
): Map<string, { yoy: number | null; qoq: number | null }> {
  const reports = distinctReports(
    earnings.filter((e) => value(e) !== null && ms(e.date) !== null),
  );
  const out = new Map<string, { yoy: number | null; qoq: number | null }>();
  reports.forEach((report, i) => {
    const now = value(report);
    const ago = yearAgo(reports, i, (e) => ms(e.date));
    out.set(report.date, {
      yoy: change(now, ago >= 0 ? value(reports[ago]!) : null),
      qoq: change(now, i > 0 ? value(reports[i - 1]!) : null),
    });
  });
  return out;
}

/**
 * The revenue a report announced: the server's matched actual where it sent
 * one, else estimate plus surprise, which is the same number by construction.
 * The screener's Rev YoY/QoQ read the vendor's actual on the same rows.
 */
export function reportedRevenue(e: EarningsQuarter): number | null {
  const actual = num(e.revenue);
  if (actual !== null) return actual;
  const estimate = num(e.revenueEstimated);
  const surprise = num(e.revenueSurprise);
  return estimate !== null && surprise !== null ? estimate + surprise : null;
}

/**
 * The YoY as the short label above an `E`: `+123%`, `-8%`, and `×12` from
 * 1,000% up so a label never passes five characters. Below -999% (a loss that
 * deepened tenfold) it reads `<-999%`. `null` gives no label.
 */
export function yoyNote(yoy: number | null | undefined): string | undefined {
  if (yoy === null || yoy === undefined || !Number.isFinite(yoy)) return undefined;
  const pct = yoy * 100;
  if (pct >= 1000) return `×${Math.round(1 + yoy)}`;
  if (pct <= -1000) return "<-999%";
  const rounded = Math.round(pct);
  return `${rounded > 0 ? "+" : ""}${rounded}%`;
}
